import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Transaction } from '@contracts/interfaces/transaction.interfaces';
import { MediaType } from '@contracts/interfaces/media.interfaces';
import { Receipt } from '@contracts/interfaces/receipt.interface';
import { ReceiptsService } from 'src/app/services/receipts.service';
import { TransactionsService } from 'src/app/services/transactions.service';

interface TransactionPreview {
  date: Date | string;
  description: string;
  amount: number;
  sha1?: string;
}

@Component({
  selector: 'app-transaction-range-dialog',
  templateUrl: './transaction-range.dialog.html',
  styleUrls: ['./transaction-range.dialog.scss', '../common-dialog-styles.scss']
})
export class TransactionRangeDialogComponent implements OnInit {
  MediaType = MediaType;
  displayedColumns = ['date', 'description', 'amount'];
  dataSource = new MatTableDataSource<TransactionPreview>([]);
  transactions: Transaction[] = [];
  selectedTransaction?: Transaction;
  receiptAmountCents: number | null = null;
  hasSearched = false;

  startDate: Date = new Date(new Date().setDate(new Date().getDate() - 7));
  endDate: Date = new Date();
  isLoading = false;
  receiptContext?: {
    date?: Date;
    description?: string;
    amount?: number;
  };
  receipt?: Receipt;
  account?: string;
  baseDate: Date = new Date();

  constructor(
    private transactionsService: TransactionsService,
    public receiptsService: ReceiptsService,
    public dialogRef: MatDialogRef<TransactionRangeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { dateRange?: { start: Date; end: Date }, receipt?: any, account?: string } | null
  ) {}

  ngOnInit(): void {
    this.baseDate = new Date();

    if (this.data?.account) {
      this.account = this.data.account;
    }

    if (this.data?.receipt) {
      this.receipt = this.data.receipt;
      this.receiptContext = {
        date: this.data.receipt.date ? new Date(this.data.receipt.date) : undefined,
        description: this.data.receipt.description || this.data.receipt.vender || this.data.receipt.file?.filename,
        amount: this.data.receipt.amount
      };
      this.receiptAmountCents = this.toCents(this.receiptContext.amount);

      if (this.receiptContext.date) {
        this.baseDate = new Date(this.receiptContext.date);
      }
    }

    // Default to amount match search if we have a receipt amount
    if (this.receiptAmountCents !== null) {
      this.findAmountMatchesOneMonth();
      return;
    }

    if (this.data?.dateRange?.start && this.data?.dateRange?.end) {
      this.setDateRange(this.data.dateRange);
      this.applyRange();
    } else {
      this.setDayRange(this.baseDate);
      this.applyRange();
    }
  }

  applyRange() {
    if (!this.startDate || !this.endDate) {
      return;
    }

    const start = new Date(this.startDate);
    const end = new Date(this.endDate);

    // Include the full days for the selected range
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    this.loadTransactionsForRange(start, end);
  }

  expandDayRangeByOne() {
    // If we have an existing range, widen it by 1 day each side; otherwise start from baseDate.
    const start = this.startDate ? new Date(this.startDate) : new Date(this.baseDate);
    const end = this.endDate ? new Date(this.endDate) : new Date(this.baseDate);

    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);

    end.setDate(end.getDate() + 1);
    end.setHours(23, 59, 59, 999);

    this.startDate = start;
    this.endDate = end;
    this.applyRange();
  }

  findAmountMatchesOneMonth() {
    if (this.receiptAmountCents === null) {
      return;
    }

    const base = this.baseDate ? new Date(this.baseDate) : new Date();
    const start = new Date(base);
    start.setMonth(start.getMonth() - 1);
    start.setHours(0, 0, 0, 0);

    const end = new Date(base);
    end.setMonth(end.getMonth() + 1);
    end.setHours(23, 59, 59, 999);

    this.startDate = start;
    this.endDate = end;

    const amounts = [this.receiptAmountCents, -this.receiptAmountCents];
    this.loadTransactionsForRangeWithQuery(start, end, { amount: { $in: amounts } });
  }

  findAmountMatches() {
    if (this.receiptAmountCents === null) {
      return;
    }

    const query: any = {
      amount: {
        $in: [this.receiptAmountCents, -this.receiptAmountCents]
      }
    };

    if (this.account) {
      query["source.account"] = this.account;
    }

    this.loadTransactionsWithQuery(query, 'Failed to load transactions for amount');
  }

  private loadTransactionsForRange(start: Date, end: Date) {
    this.loadTransactionsForRangeWithQuery(start, end);
  }

  private loadTransactionsForRangeWithQuery(start: Date, end: Date, queryOverrides: any = {}) {
    this.isLoading = true;
    this.hasSearched = false;
    const query: any = {
      date: {
        '$gte': start,
        '$lte': end
      },
      amount: { $lt: 0 }
    };

    if (queryOverrides.amount !== undefined) {
      query.amount = queryOverrides.amount;
    }

    if (this.account) {
      query["source.account"] = this.account;
    }

    this.loadTransactionsWithQuery(query, 'Failed to load transactions for range');
  }

  private loadTransactionsWithQuery(query: any, errorMessage: string) {
    this.isLoading = true;
    this.hasSearched = false;

    this.transactionsService.find(
      query,
      undefined,
      undefined,
      { date: -1 }
    ).subscribe({
      next: (transactions) => {
        this.transactions = transactions;
        this.dataSource.data = this.mapTransactions(transactions);
        this.selectedTransaction = undefined;
        this.hasSearched = true;
      },
      error: (err) => {
        console.error(errorMessage, err);
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  private setDateRange(dateRange?: { start?: string | Date; end?: string | Date }) {
    if (dateRange?.start) {
      this.startDate = new Date(dateRange.start);
      this.startDate.setHours(0, 0, 0, 0);
      this.baseDate = new Date(this.startDate);
    }

    if (dateRange?.end) {
      this.endDate = new Date(dateRange.end);
      this.endDate.setHours(23, 59, 59, 999);
    }
  }

  private setDayRange(day: Date) {
    const start = new Date(day);
    start.setHours(0, 0, 0, 0);

    const end = new Date(day);
    end.setHours(23, 59, 59, 999);

    this.startDate = start;
    this.endDate = end;
  }

  private mapTransactions(transactions: Transaction[] = []): TransactionPreview[] {
    return transactions.map(transaction => ({
      date: transaction.date ? new Date(transaction.date) : '',
      description: transaction.description || transaction.source?.original?.Description || '',
      amount: transaction.amount,
      sha1: transaction.source?.sha1
    }));
  }

  private toCents(amount?: string | number | null): number | null {
    if (amount === undefined || amount === null) {
      return null;
    }

    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) {
      return null;
    }

    return Math.round(numericAmount * 100);
  }

  isAmountMatch(transaction: TransactionPreview): boolean {
    if (this.receiptAmountCents === null) {
      return false;
    }

    const txAmount = Math.round(transaction.amount ?? 0);
    return txAmount === this.receiptAmountCents || txAmount === -this.receiptAmountCents;
  }

  selectTransaction(preview: TransactionPreview, index: number) {
    this.selectedTransaction = this.transactions[index] || this.transactions.find(tx => tx.source?.sha1 === preview.sha1);
  }

  matchTransaction(preview: TransactionPreview, index: number) {
    this.selectTransaction(preview, index);
    this.useSelected();
  }

  isSelected(preview: TransactionPreview, index: number): boolean {
    const selectedSha1 = this.selectedTransaction?.source?.sha1;
    const previewSha1 = preview.sha1;
    if (selectedSha1 && previewSha1) {
      return selectedSha1 === previewSha1;
    }
    return !!this.selectedTransaction && this.transactions[index] === this.selectedTransaction;
  }

  useSelected() {
    if (!this.selectedTransaction) {
      return;
    }

    this.dialogRef.close(this.selectedTransaction);
  }

  human(amount: number) {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format((amount || 0) / 100);
  }

  close() {
    this.dialogRef.close();
  }
}
